import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, firstValueFrom, from, throwError } from 'rxjs';
import { FtFileType } from 'src/app/model/response/file-map-response';
import { MapPasswordCryptoService } from 'src/app/service/map-password-crypto.service';

/** Estado do modal de senha. `null` = fechado. Um único modal para o app inteiro. */
export interface MapPasswordPrompt {
  fileType: FtFileType;
  /** true depois de uma senha recusada: o modal mostra "senha incorreta". */
  invalid: boolean;
  /** true enquanto cifra e repete a leitura: inputs desabilitados, sem fechar. */
  busy: boolean;
}

/** Erro já normalizado pelos serviços de readlog (`FileMapError`/`FileChannelsError`). */
interface ReadlogError {
  errorCode?: string;
}

/** Códigos que pedem senha (contratos VISIONFT/POWERFT_PASSWORD_FRONT_CONTRACT §2). */
const PASSWORD_ERROR_CODES = new Set(['MAP_PASSWORD_PROTECTED', 'MAP_PASSWORD_INVALID']);

export function isMapPasswordError(err: unknown): boolean {
  const code = (err as ReadlogError | null)?.errorCode;
  return typeof code === 'string' && PASSWORD_ERROR_CODES.has(code);
}

/**
 * Gate de senha por arquivo (`user_id + source_file_id`), no mesmo padrão de "serviço root +
 * modal hospedado no shell" do `PlanModalService`.
 *
 * `guard()` embrulha uma leitura do readlog: se ela falhar com erro de senha, abre o modal,
 * cifra a senha digitada e repete a MESMA leitura com `password`. Regras dos contratos:
 * - nunca repetir 422 sozinho — só depois de uma senha fornecida/corrigida;
 * - cancelar encerra a tentativa: o erro original volta para quem chamou;
 * - várias leituras do mesmo arquivo em paralelo dividem UM modal; depois do desbloqueio, as
 *   que ficaram esperando repetem SEM senha (o cache do proprietário já abre sem ela);
 * - a senha em claro vive só dentro deste fluxo: não vai para URL, log nem storage.
 */
@Injectable({ providedIn: 'root' })
export class MapPasswordService {
  readonly prompt$ = new BehaviorSubject<MapPasswordPrompt | null>(null);

  /** Resposta pendente do modal aberto (uma por vez). */
  private answer: ((password: string | null) => void) | null = null;
  /** Fila para que dois arquivos diferentes nunca disputem o mesmo modal. */
  private promptQueue: Promise<unknown> = Promise.resolve();
  /** Desbloqueio em andamento por arquivo: `true` = destravou, `false` = cancelou/falhou. */
  private unlocking = new Map<string, Promise<boolean>>();

  constructor(private crypto: MapPasswordCryptoService) {}

  guard<T>(
    key: string,
    fileType: FtFileType,
    attempt: (password?: string) => Observable<T>,
  ): Observable<T> {
    return attempt().pipe(
      catchError((err: unknown) => {
        if (!isMapPasswordError(err)) return throwError(() => err);
        return from(this.retryAfterUnlock(key, fileType, attempt, err));
      }),
    );
  }

  /** Chamado pelo modal com a senha digitada (sem trim — contrato). */
  submit(password: string): void {
    this.answer?.(password);
  }

  /** Chamado pelo modal ao cancelar/fechar. */
  cancel(): void {
    this.answer?.(null);
  }

  private async retryAfterUnlock<T>(
    key: string,
    fileType: FtFileType,
    attempt: (password?: string) => Observable<T>,
    original: unknown,
  ): Promise<T> {
    const pending = this.unlocking.get(key);
    if (pending) {
      // Outra leitura do mesmo arquivo já está pedindo a senha: espera e repete sem senha.
      if (await pending) return firstValueFrom(attempt());
      throw original;
    }

    let finishUnlock!: (unlocked: boolean) => void;
    this.unlocking.set(key, new Promise<boolean>((resolve) => (finishUnlock = resolve)));

    try {
      let invalid = false;
      for (;;) {
        const password = await this.ask(fileType, invalid);
        if (password === null) {
          finishUnlock(false);
          throw original;
        }

        this.prompt$.next({ fileType, invalid, busy: true });
        try {
          const encrypted = await this.crypto.encrypt(password);
          const result = await firstValueFrom(attempt(encrypted));
          finishUnlock(true);
          return result;
        } catch (err) {
          if (isMapPasswordError(err)) {
            invalid = true;
            continue;
          }
          finishUnlock(false);
          throw err;
        }
      }
    } finally {
      this.unlocking.delete(key);
      this.prompt$.next(null);
    }
  }

  /** Abre o modal e resolve com a senha digitada, ou `null` no cancelamento. */
  private ask(fileType: FtFileType, invalid: boolean): Promise<string | null> {
    const turn = this.promptQueue.then(
      () =>
        new Promise<string | null>((resolve) => {
          this.answer = (password) => {
            this.answer = null;
            resolve(password);
          };
          this.prompt$.next({ fileType, invalid, busy: false });
        }),
    );
    this.promptQueue = turn.catch(() => undefined);
    return turn;
  }
}
